/**
 * Phase 16 Plan 16-03 — TanStack Query hooks for the logsheets-maker service,
 * specifically the Plan 16-02 D-151 endpoint for active Work Orders.
 *
 * Phase 17 Plan 17-02 — extended with useMakerKpisForWorkOrderMachineQuery (D-162).
 *
 * Mirrors the pattern of useLogsheetApproverQueries.ts.
 */

import { useQuery } from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/error';
import {
  getMakerActiveWorkOrdersRequest,
  getMakerKpisForWorkOrderMachineRequest,
} from '@/services/logsheets-maker';
import type { MakerKpiForMachine, MakerWorkOrderListItem } from '@/types/logsheets-maker';

/**
 * Fetches all Active Work Orders (no siteId filter).
 * D-229: WO is now the first selection in the maker wizard; sites are scoped
 * to the chosen WO's active sites.
 *
 * @param opts.enabled - Additional enabled flag (defaults to true).
 */
export function useMakerActiveWorkOrdersQuery(
  opts?: { readonly enabled?: boolean },
): ReturnType<typeof useQuery<readonly MakerWorkOrderListItem[], ApiError>> {
  return useQuery<readonly MakerWorkOrderListItem[], ApiError>({
    queryKey: ['logsheets-maker', 'work-orders'],
    queryFn: () => getMakerActiveWorkOrdersRequest(),
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  });
}

/**
 * Phase 17 Plan 17-02 D-162 — Fetches the KPIs applicable to a specific
 * (Work Order, Machine) pair. Used by the maker wizard KPI rendering switch.
 *
 * Pass null for either param to disable — no non-null assertion used (Rule 2
 * guard, mirrors Phase 16-03 deviation #4).
 *
 * @param woId - Work order ID. Pass null to disable.
 * @param machineId - Machine ID. Pass null to disable.
 */
export function useMakerKpisForWorkOrderMachineQuery(
  woId: number | null,
  machineId: number | null,
): ReturnType<typeof useQuery<readonly MakerKpiForMachine[], ApiError>> {
  return useQuery<readonly MakerKpiForMachine[], ApiError>({
    queryKey: ['logsheets-maker', 'kpis-for-machine', woId, machineId],
    queryFn: () => {
      if (woId === null) throw new Error('woId is required');
      if (machineId === null) throw new Error('machineId is required');
      return getMakerKpisForWorkOrderMachineRequest(woId, machineId);
    },
    enabled: woId !== null && machineId !== null,
    staleTime: 15_000,
  });
}
